import _ from 'lodash';
import moment from 'moment';
import { getColor } from 'common/constants/_colors';
import { LICENSES, SEARCH_DATE_FORMAT } from 'common/constants/constants';
import AnnotationMarker from 'images/icons/annotation_marker.svg';
import { getAnnotationTooltipText, toolTipFormatter } from './Tooltip';

// We need to calculate Highchart last tick line to show Annotation Marker
// (Highchart doesn't always match yAxis > max with the last tick line)
// Ceiling to the next nearest decimal and getting ceiled tick intervals and max
export const getGraphMaxPoint = (maxPoint = 0, numOfTicks = 6) => {
  if (!maxPoint) return { graphMaxPoint: 1, tickInterval: 0, tickAmount: 1 };

  const maxPointCeiled = _.ceil(
    maxPoint,
    String(_.round(maxPoint)).length <= 2 ? 0 : -1
  );
  const tickInterval = _.ceil(maxPointCeiled / (numOfTicks - 1));
  const graphMaxPoint = tickInterval * (numOfTicks - 1);

  return { graphMaxPoint, tickInterval, tickAmount: numOfTicks };
};

// loop over Annotations and check if annotation created_at isBetween graphStackBarDates
// save stackBarIndex in annotationPoints
export const getAnnotationPoints = (
  metricAnnotation,
  graphStackBarDates,
  metricsSettingName
) => {
  const annotationPoints = [];
  metricAnnotation.forEach(({ created_at, metadata }) => {
    const createdAtFormatted = moment(created_at).format(SEARCH_DATE_FORMAT);
    graphStackBarDates.forEach(({ after, before }, stackBarIndex) => {
      if (moment(createdAtFormatted).isBetween(after, before, null, '[)')) {
        annotationPoints.push({
          stackBarIndex,
          created_at,
          metadata: metadata[metricsSettingName]
        });
      }
    });
  });

  return annotationPoints;
};

// Calculate Annotations position based on StackedBar series
export function mapMetricsToAnnotationSeries({
  graphId,
  granularity,
  series,
  maxPoint = 0,
  metricsSettingName,
  annotations = []
} = {}) {
  if (!annotations.length) return {};

  const metricAnnotation = _.orderBy(
    annotations.filter(item => item.metadata[metricsSettingName]),
    'created_at',
    ['desc']
  );

  if (!metricAnnotation.length) return {};

  const graphStackBarDates = _.get(series, '[0].dateRange', []);
  const annotationPoints = getAnnotationPoints(
    metricAnnotation,
    graphStackBarDates,
    metricsSettingName
  );
  // groupBy stackBarIndex (showing only one annotation per StackBar)
  const groupedAnnotationPoints = _.groupBy(annotationPoints, 'stackBarIndex');
  // map annotations for Highchart: array of arrays of [xIndex, yIndex]
  const annotationData = Object.keys(groupedAnnotationPoints).map(item => [
    _.toNumber(item),
    maxPoint
  ]);

  return {
    type: 'line',
    lineWidth: 0,
    lineColor: 'transparent',
    zIndex: 1,
    marker: {
      enabled: true,
      symbol: `url(${AnnotationMarker})`,
      width: 10,
      height: 8
    },
    data: annotationData,
    states: {
      hover: {
        enabled: false
      }
    },
    tooltip: {
      snap: 0,
      headerFormat: undefined,
      pointFormatter() {
        const annotationPoint = groupedAnnotationPoints[this.x] || [];
        const created = _.get(annotationPoint, `[0]created_at`, moment());
        const threshholds = _.get(annotationPoint, `[0]metadata`, {});
        const changesPerPreiod = annotationPoint.length;
        return toolTipFormatter({
          title: null,
          text: getAnnotationTooltipText({
            graphId,
            granularity,
            created,
            threshholds,
            changesPerPreiod
          })
        });
      }
    }
  };
}

export const formatAnnotationsToPlotLines = annotationMarkers =>
  _.get(annotationMarkers, 'data', []).map(item => {
    return {
      color: getColor('nepal'),
      dashStyle: 'shortdot',
      width: 2,
      value: item[0],
      zIndex: 100
    };
  });

export const getStackedGraphSettings = ({
  metricsConfig = {},
  metrics = []
}) => {
  const stackedSeries = metricsConfig.stackGraphMapper(metrics);
  const metricsSettingName = metricsConfig.settingName;
  const maxPoint = metricsConfig.maxPoint(metrics);

  return {
    stackedSeries,
    metricsSettingName,
    ...getGraphMaxPoint(maxPoint)
  };
};

export const LICENSE_SHOW_EMPTY_STATE_FOR_TEAM_GOALS = [LICENSES.FREE];
